// Tenant-wide data-readiness summary (Increment B3): headline counts of who is
// payroll-ready vs missing statutory IDs / scanner mapping / duplicates.
// Mounted on the Employees page for anyone with employee:write.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { GaugeCircle, Loader2 } from 'lucide-react';
import { getReadinessSummary, type ReadinessSummary } from '@/lib/hrApi';

const Stat: React.FC<{ label: string; value: number; tone?: 'default' | 'good' | 'warn' | 'bad' }> = ({ label, value, tone = 'default' }) => {
  const valueCls =
    tone === 'good' ? 'text-success'
      : tone === 'warn' ? 'text-warning'
        : tone === 'bad' ? 'text-danger'
          : 'text-foreground';
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2.5 min-w-[110px]">
      <p className={`text-xl font-bold leading-tight ${valueCls}`}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
};

export const DataReadinessSummaryCard: React.FC = () => {
  const { t } = useTranslation('employees');
  const [summary, setSummary] = useState<ReadinessSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    getReadinessSummary()
      .then((s) => { if (active) setSummary(s); })
      .catch(() => { if (active) setFailed(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Quietly disappear if the endpoint isn't reachable — this is an assist
  // surface, not a blocker for the directory itself.
  if (failed) return null;

  const c = summary?.counts;

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base text-orange-700 flex items-center gap-2">
          <GaugeCircle className="w-4 h-4" /> {t('readiness.summary_title', { defaultValue: 'Data readiness' })}
          {summary && (
            <span className="text-xs font-normal text-muted-foreground">
              {t('readiness.summary_avg', { defaultValue: 'average {{score}}%', score: Math.round(summary.average_score) })}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading || !c ? (
          <div className="py-3 flex items-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('readiness.summary_loading', { defaultValue: 'Checking employee records…' })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            <Stat label={t('readiness.stat_total', { defaultValue: 'Employees' })} value={c.total} />
            <Stat label={t('readiness.stat_complete', { defaultValue: 'Complete' })} value={c.complete} tone="good" />
            <Stat label={t('readiness.stat_missing_tin', { defaultValue: 'Missing TIN' })} value={c.missing_tin} tone={c.missing_tin ? 'warn' : 'good'} />
            <Stat label={t('readiness.stat_missing_nssf', { defaultValue: 'Missing NSSF' })} value={c.missing_nssf} tone={c.missing_nssf ? 'warn' : 'good'} />
            <Stat label={t('readiness.stat_missing_scanner', { defaultValue: 'Missing scanner ID' })} value={c.missing_scanner_id} tone={c.missing_scanner_id ? 'warn' : 'good'} />
            <Stat label={t('readiness.stat_missing_contract', { defaultValue: 'Missing contracts' })} value={c.missing_contract ?? 0} tone={c.missing_contract ? 'warn' : 'good'} />
            <Stat label={t('readiness.stat_missing_documents', { defaultValue: 'Missing documents' })} value={c.missing_documents ?? 0} tone={c.missing_documents ? 'warn' : 'good'} />
            <Stat label={t('readiness.stat_duplicates', { defaultValue: 'With duplicates' })} value={c.with_duplicates} tone={c.with_duplicates ? 'bad' : 'good'} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataReadinessSummaryCard;
